import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Package, 
  FileText, 
  Upload,
  FileCheck,
  BarChart3,
  ArrowRightLeft,
  Loader2,
  TrendingUp,
  Warehouse,
  DollarSign,
  AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InventoryDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalMaterials: 0,
    totalStockValue: 0,
    materialDocuments: 0,
    openGRIR: 0,
    grirVariance: 0
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const [stockRes, matDocRes, grirRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/stock-overview?include_zero_stock=false`),
        axios.get(`${BACKEND_URL}/api/material-documents?limit=100`),
        axios.get(`${BACKEND_URL}/api/grir-reconciliation/CC001`)
      ]);

      const stocks = stockRes.data;
      const totalValue = stocks.reduce((sum, s) => sum + (s.total_value || 0), 0);
      
      setStats({
        totalMaterials: stocks.length,
        totalStockValue: totalValue,
        materialDocuments: matDocRes.data.length,
        openGRIR: grirRes.data.open_items_count || 0,
        grirVariance: grirRes.data.total_variance || 0
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    {
      title: 'Stock Overview',
      description: 'View current stock levels and valuation (MMBE)',
      icon: <Warehouse className="h-8 w-8" />,
      path: '/inventory/stock-overview',
      color: 'bg-orange-500'
    },
    {
      title: 'Material Documents',
      description: 'View all stock movement history',
      icon: <FileText className="h-8 w-8" />,
      path: '/inventory/material-documents',
      color: 'bg-green-500'
    },
    {
      title: 'Initial Stock',
      description: 'Post initial stock (561) - MB1C',
      icon: <Upload className="h-8 w-8" />,
      path: '/inventory/initial-stock',
      color: 'bg-orange-500'
    },
    {
      title: 'Stock Transfer',
      description: 'Transfer stock between locations (301/311)',
      icon: <ArrowRightLeft className="h-8 w-8" />,
      path: '/inventory/stock-transfer',
      color: 'bg-orange-500'
    },
    {
      title: 'GR/IR Reconciliation',
      description: 'Reconcile goods receipts with invoices (MR11)',
      icon: <FileCheck className="h-8 w-8" />,
      path: '/inventory/grir-reconciliation',
      color: 'bg-orange-500'
    },
    {
      title: 'Accounting Documents',
      description: 'View FI posting documents',
      icon: <DollarSign className="h-8 w-8" />,
      path: '/inventory/accounting-documents',
      color: 'bg-orange-500'
    }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Inventory Management</h1>
          <p className="text-gray-600 mt-2">SAP MM - Stock Management & Financial Integration</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Stock Items</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats.totalMaterials}
                </p>
              </div>
              <Package className="h-10 w-10 text-orange-500 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Stock Value</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : formatCurrency(stats.totalStockValue)}
                </p>
              </div>
              <TrendingUp className="h-10 w-10 text-green-500 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Material Docs</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats.materialDocuments}
                </p>
              </div>
              <FileText className="h-10 w-10 text-orange-500 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Open GR/IR Items</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats.openGRIR}
                </p>
              </div>
              <FileCheck className="h-10 w-10 text-orange-500 opacity-50" />
            </div>
          </div>

          <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${stats.grirVariance !== 0 ? 'border-red-500' : 'border-orange-500'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">GR/IR Variance</p>
                <p className={`text-2xl font-bold ${stats.grirVariance !== 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : formatCurrency(stats.grirVariance)}
                </p>
              </div>
              <AlertCircle className={`h-10 w-10 ${stats.grirVariance !== 0 ? 'text-red-500' : 'text-orange-500'} opacity-50`} />
            </div>
          </div>
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {menuItems.map((item, index) => (
            <div
              key={index}
              onClick={() => navigate(item.path)}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all cursor-pointer border border-gray-100 hover:border-gray-200 overflow-hidden"
            >
              <div className={`${item.color} p-4 text-white`}>
                {item.icon}
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => navigate('/inventory/initial-stock')} className="bg-orange-600 hover:bg-orange-700">
              <Upload className="h-4 w-4 mr-2" /> Post Initial Stock
            </Button>
            <Button onClick={() => navigate('/inventory/stock-transfer')} variant="outline">
              <ArrowRightLeft className="h-4 w-4 mr-2" /> Stock Transfer
            </Button>
            <Button onClick={() => navigate('/inventory/grir-reconciliation')} variant="outline">
              <FileCheck className="h-4 w-4 mr-2" /> Run GR/IR Clearing
            </Button>
            <Button onClick={() => navigate('/p2p/gr-create')} variant="outline">
              <Package className="h-4 w-4 mr-2" /> Post Goods Receipt
            </Button>
          </div>
        </div>

        {/* Back to P2P Link */}
        <div className="mt-6 text-center">
          <Button variant="link" onClick={() => navigate('/p2p')} className="text-gray-600">
            ← Back to Procure to Pay
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default InventoryDashboard;
